import { Grid, useMediaQuery, useTheme, Box, Fade } from "@mui/material";
import PageTitle from "../../../../utils/PageTitle";
import {
  selectExistingOrdersWithoutCancelled,
  selectRiskAssessments
} from "../../../../store/reducers/order/OrderInformationSlice";
import { useSelector } from "react-redux";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";
import ResultsSlider from "components/v3/pages/TestResultsPage/ResultsSlider";
import { selectUserPersonalInformation } from "store/reducers/user/UserAccountSlice";
import { useHistory } from "react-router-dom";
import WhatsNextBox from "components/v2/Pages/TestResultsPage/WhatsNextBox";
import ResultsSlidesPageStyles from "../ResultsSlidesPage/ResultsSlidesPage.styles";
import TestResultsDNA from "assets/images/test-results-dna-strand.svg";
import { useEffect, useState } from "react";
import { composeSlides } from "../ResultsSlidesPage/slides";
import { PATHS } from "constants/pathConstants";
import {
  trackResultsReviewCompleted,
  trackResultsReviewStarted
} from "analytics";

interface ResultsMoreInfoSlidePageProps {
  slideIds?: string[];
}

const ResultsMoreInfoSlidePage = ({
  slideIds
}: ResultsMoreInfoSlidePageProps) => {
  const theme = useTheme();
  const screenWidthIsMdOrSmaller = useMediaQuery(theme.breakpoints.down("md"));
  const user = useSelector(selectUserPersonalInformation);
  const history = useHistory();
  const classes = ResultsSlidesPageStyles();
  const [activeSlide, setActiveSlide] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const orders = useSelector(selectExistingOrdersWithoutCancelled);
  const ordersWithDocs = orders.filter((order: any) => {
    return order?.documentIds?.length > 0 && order?.glTestType === "GHS";
  });
  const testResultsUrl = `${PATHS.TEST_RESULTS_PAGE}/${ordersWithDocs[0].glOrderId}`;
  PageTitle("More info about results slides");

  const riskAssessment = useSelector(selectRiskAssessments);

  useEffect(() => {
    trackResultsReviewStarted("more_info");
  }, []);

  const handleSlideChange = (slideId: string) => {
    setFadeIn(false);
    setTimeout(() => {
      setActiveSlide(slideId);
      setFadeIn(true);
    }, 150);
  };

  const slides = composeSlides(
    user,
    riskAssessment,
    history,
    classes,
    testResultsUrl,
    slideIds || ["more-info-intro", "gene-count", "more-info-outro"]
  );

  const handleStepChange = (step: number) => {
    setActiveStep(step);
    if (step === slides.length - 1) {
      trackResultsReviewCompleted({
        slideSet: "more_info",
        slidesViewedCount: slides.length
      });
    }
  };

  const containedSlides = () => {
    if (screenWidthIsMdOrSmaller) {
      return (
        <ResultsSlider
          slides={slides}
          initialSlide={activeStep}
          onSlideChange={(slideId) => {
            handleSlideChange(slideId);
          }}
          onStepChange={handleStepChange}
        />
      );
    } else {
      return (
        <Grid
          container
          direction="row"
          justifyContent="space-around"
          className={classes.wideModeContainer}
        >
          <Grid item md={7} lg={5} className={classes.wideModeSlidesContainer}>
            <ResultsSlider
              slides={slides}
              initialSlide={activeStep}
              onSlideChange={(slideId) => {
                handleSlideChange(slideId);
              }}
              onStepChange={handleStepChange}
            />
          </Grid>
          <Grid
            item
            md={4}
            lg={5}
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%"
            }}
          >
            <Fade in={fadeIn} timeout={150}>
              <Box sx={{ width: "100%" }}>
                {activeSlide === "more-info-outro-slide" ? (
                  <WhatsNextBox smallMode={false} />
                ) : (
                  <img
                    src={TestResultsDNA}
                    style={{
                      width: "100%",
                      maxHeight: "400px",
                      objectFit: "contain"
                    }}
                    alt="Test Results DNA"
                  />
                )}
              </Box>
            </Fade>
          </Grid>
        </Grid>
      );
    }
  };

  return (
    <GradientFramedLayout
      showHeader={true}
      showFooter={false}
      showNav={true}
      makeLogoClickable={true}
    >
      <Grid container className={classes.container}>
        {containedSlides()}
      </Grid>
    </GradientFramedLayout>
  );
};

export default ResultsMoreInfoSlidePage;
